import {
  addSlot,
  alignGroupSetRows,
  isEffortAxis,
  kindAt,
  setRowKind,
  setSlotGroup,
  setWorkKind,
  trimGroupSetRows,
  updateCell,
  updateSetRowNote,
  updateSlot,
  uuidv4,
  type BlockSetRow,
  type CellDraft,
  type Draft,
  type IntensityKind,
  type SetRowDraft,
} from "@heraion/shared";

// The phone editor's writes that are more than one shared action (#336).
// Pure `(draft, …) => Draft`, so each is one undo step through setDraft.

/** Where one round of one exercise lives in the draft. */
export type RoundAddr = { sIdx: number; slotIdx: number; week: number };

const CELL_KEYS = ["reps", "amrap", "amrapStop", "repsMin", "repsMax", "intensityValue", "workDurationSec", "restSec"] as const;

/** A set-table row edit (ComposerSetTable speaks BlockSetRow) written back
 *  onto the draft. The cell fields land on this round only. `workKind` and
 *  the axis live on the ROW, so they change every round: the same rule the
 *  draft has always had (SetRowDraft). */
export const applyRoundRowPatch = (
  draft: Draft,
  at: RoundAddr,
  rowIdx: number,
  patch: Partial<BlockSetRow>,
): Draft => {
  let next = draft;
  const cell: Partial<CellDraft> = {};
  for (const k of CELL_KEYS) if (k in patch) Object.assign(cell, { [k]: patch[k] });
  if (Object.keys(cell).length > 0) {
    next = updateCell(next, at.sIdx, at.slotIdx, rowIdx, at.week, cell);
  }
  if ("workKind" in patch) {
    next = setWorkKind(next, at.sIdx, at.slotIdx, rowIdx, patch.workKind);
    // The bound is on the ROW, so a duration cleared with it is cleared in
    // every round: left in the others it comes back when «Time» is picked.
    if ("workDurationSec" in patch && patch.workDurationSec === undefined) {
      const cells = next.sessions[at.sIdx]?.slots[at.slotIdx]?.setRows[rowIdx]?.cells ?? [];
      for (const w of cells.keys()) {
        if (w !== at.week) next = updateCell(next, at.sIdx, at.slotIdx, rowIdx, w, { workDurationSec: undefined });
      }
    }
  }
  if (patch.note !== undefined) {
    next = updateSetRowNote(next, at.sIdx, at.slotIdx, rowIdx, at.week, patch.note);
  }
  return next;
};

/** The load axis for every set of the exercise (LoadAxisSelector above the
 *  table), tapped in round `week`. setRowKind re-seeds each value for the new
 *  axis; a set whose round already shows `kind` is left alone, and so is an
 *  RPE set on an RIR pick, or the reverse (#432). */
export const setSlotAxis = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  kind: IntensityKind,
  week?: number,
): Draft => {
  const rows = draft.sessions[sIdx]?.slots[slotIdx]?.setRows ?? [];
  const keep = (row: SetRowDraft) =>
    isEffortAxis(kind) && isEffortAxis(week === undefined ? row.intensityKind : kindAt(row, week));
  return rows.reduce((d, row, rowIdx) => (keep(row) ? d : setRowKind(d, sIdx, slotIdx, rowIdx, kind, week)), draft);
};

/** New exercises at the end of day `sIdx`, as the picker returns them.
 *  Same composition as `add_exercise` in prescribe/prescribeDispatch.ts: the
 *  new slot lands last. `setRows`, when given, replaces the blank seed (the
 *  day role's starting values, 2B). */
export const appendExercises = (
  draft: Draft,
  sIdx: number,
  picks: ReadonlyArray<{ exerciseId: string; exerciseName: string; setRows?: SetRowDraft[] }>,
): Draft =>
  picks.reduce((d, pick) => {
    const at = d.sessions[sIdx]?.slots.length;
    if (at === undefined) return d;
    return updateSlot(addSlot(d, sIdx), sIdx, at, pick);
  }, draft);

/** Pair the exercise with the next one in the day. A next exercise already in
 *  a superset is joined, name included (as the old editor's set_group kept
 *  it); otherwise a new superset is minted, with no name. Set counts are then
 *  evened out: padded up (alignGroupSetRows, as the save would) or, when the
 *  author chose so, trimmed down (trimGroupSetRows). */
export const supersetWithNext = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  align: "pad" | "trim" = "pad",
): Draft => {
  const slots = draft.sessions[sIdx]?.slots ?? [];
  const next = slots[slotIdx + 1];
  if (!slots[slotIdx] || !next) return draft;
  const group = next.group ?? { id: uuidv4(), kind: "superset" as const };
  let d = setSlotGroup(draft, sIdx, slotIdx, group);
  if (!next.group) d = setSlotGroup(d, sIdx, slotIdx + 1, group);
  return align === "trim" ? trimGroupSetRows(d, sIdx, group.id) : alignGroupSetRows(d, sIdx, group.id);
};
